import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Eye, ShoppingCart, X } from "lucide-react";
import Swal from "sweetalert2";

export default function ChildCategoryProducts({
  data,
  setWishlistCount,
  fetchMenuCounts,
  setCartCount,
}) {
  return (
    <div className="container mx-auto py-2">
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-6">
        {data.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            setWishlistCount={setWishlistCount}
            fetchMenuCounts={fetchMenuCounts}
            setCartCount={setCartCount}
          />
        ))}
      </div>
    </div>
  );
}

function ProductCard({
  product,
  setWishlistCount,
  fetchMenuCounts,
  setCartCount,
}) {
  const [hoverImages, setHoverImages] = useState({});
  const [relatedHoverImages, setRelatedHoverImages] = useState({});
  const [popupImage, setPopupImage] = useState(null);
  // 🔹 Color Hover Handling
  const handleColorHover = (productId, image) => {
    if (image) setHoverImages((prev) => ({ ...prev, [productId]: image }));
  };

  const handleColorLeave = (productId) => {
    setHoverImages((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };
  const handleAddToWishlist = async (e, product) => {
    e.stopPropagation();
    if (!product?.id) return;

    const payload = {
      product_id: product.id,
      color_id: product?.variants?.[0]?.color_id || null,
      size_id: product?.variants?.[0]?.size_id || null,
      quantity: 1,
    };

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let id = localStorage.getItem("authUserId");

      let url = "https://tyka.premierhostings.com/beta/api/wishlists";
      let headers = { "Content-Type": "application/json" };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setWishlistCount((prev) => prev + 1); // ✅ Guest update instantly
        } else {
          fetchMenuCounts(id, authToken);
        }

        Swal.fire("💖 Added to Wishlist!", "", "success");
      } else {
        Swal.fire(
          "Failed!",
          data.message || "Failed to add to wishlist",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Something went wrong!", "error");
    }
  };
  const handleAddToCart = async (e, product) => {
    e.stopPropagation();
    if (!product?.id) return;

    const payload = {
      item_type: "product",
      product_id: product.id,
      color_id: product?.variants?.[0]?.color_id || null,
      size_id: product?.variants?.[0]?.size_id || null,
      quantity: 1,
    };

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let url = `https://tyka.premierhostings.com/beta/api/cart`;
      let headers = { "Content-Type": "application/json" };
      let id = localStorage.getItem("authUserId");
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setCartCount((prev) => prev + 1); // ✅ Guest update instantly
        } else {
          fetchMenuCounts(id, authToken);
          fetchMenuCounts(); // ✅ Logged in update via API
        }

        Swal.fire("✅ Added to Cart!", "", "success");
      } else {
        Swal.fire("Failed!", data.message || "Failed to add to cart", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Something went wrong!", "error");
    }
  };

  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/${product.slug}`)}
      className="bg-white  transition overflow-hidden cursor-pointer"
    >
      {/* Image */}
      <div className="group relative">
        {/* Product Image */}
        <img
          src={
            hoverImages[product.id] ||
            relatedHoverImages[product.id] ||
            product.default_image ||
            "/placeholder.png"
          }
          alt={product.name}
          className="w-full object-contain border"
        />

        {/* FULL-HEIGHT TRANSPARENT COLOR OVERLAY (scoped to image only) */}
        <div className="pointer-events-none absolute inset-0 z-10 flex">
          {(product.variants
            ? [
                ...new Map(
                  product.variants.map((v) => [v.color?.id, v]),
                ).values(),
              ]
            : []
          ).map((variant, index) => {
            const color = variant.color;
            const colorHex = color?.hexa_code;
            const colorName = color?.name?.toLowerCase();
            const variantImage = variant.variant_default_image;

            const fallback = colorHex
              ? colorHex
              : {
                  red: "#ef4444",
                  blue: "#3b82f6",
                  green: "#22c55e",
                  black: "#000000",
                  white: "#ffffff",
                  yellow: "#facc15",
                  gray: "#6b7280",
                  "royal blue": "#1d4ed8",
                }[colorName] || "#9ca3af";

            return (
              <div
                key={index}
                className="relative w-full h-full cursor-pointer pointer-events-auto"
                onMouseEnter={() => handleColorHover(product.id, variantImage)}
                onMouseLeave={() => handleColorLeave(product.id)}
                style={{ backgroundColor: "transparent" }}
              >
                {/* Visible 4px strip at the bottom */}
                <div
                  className="absolute left-0 right-0 bottom-0 h-[4px]"
                  style={{
                    backgroundColor: fallback,
                    borderTop:
                      colorName === "white" ? "1px solid #e5e7eb" : "none",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Discount Badge - Left */}
        {Number(product.discount?.amount) > 0 && (
          <div className="absolute top-4 left-3 z-20">
            <span className="bg-white text-green-600 border border-green-600 text-xs font-bold px-2 py-1 rounded">
              {product.discount.type === "percentage"
                ? `${parseInt(product.discount.amount)}% OFF`
                : `₹${parseInt(product.discount.amount)} OFF`}
            </span>
          </div>
        )}

        {/* Hover Buttons */}
        <div className="absolute top-4 right-3 z-20 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={(e) => handleAddToCart(e, product)}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-[#FF0009] hover:text-white transition-all duration-300 cursor-pointer"
          >
            <ShoppingCart size={18} />
          </button>

          <button
            onClick={(e) => handleAddToWishlist(e, product)}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-[#FF0009] hover:text-white transition-all duration-300 cursor-pointer"
          >
            <Heart size={18} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setPopupImage(
                hoverImages[product.id] ||
                  relatedHoverImages[product.id] ||
                  product.default_image,
              );
            }}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-[#FF0009] hover:text-white transition-all duration-300 cursor-pointer"
          >
            <Eye size={18} />
          </button>
        </div>
      </div>

      {/* INFO (outside the media wrapper so it won't be covered) */}
      <div className="text-left p-2">
        {/* Optional: keep a thin color bar below the image too */}
        {/* {colorBar(product)} */}

        <h4 className="text-sm sm:text-base font-semibold">{product.name}</h4>

        {product.cost_price && product.selling_price && product.discount ? (
          <div className="flex items-center gap-3">
            <span className="text-lg text-gray-500 line-through">
              ₹{product.cost_price}
            </span>
            <span className="text-lg font-bold text-red-600">
              ₹{product.selling_price}
            </span>
          </div>
        ) : (
          <p className="text-red-600 font-bold">
            ₹
            {product.selling_price ||
              product.price ||
              product.cost_price ||
              "N/A"}
          </p>
        )}
      </div>
      {/* Image Popup Modal */}
      {popupImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setPopupImage(null)}
        >
          <div
            className="relative max-w-2xl w-3/4 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 bg-white rounded-full p-1"
              onClick={() => setPopupImage(null)}
            >
              <X size={20} />
            </button>
            <img
              src={popupImage}
              alt="Product Preview"
              className="w-full h-auto max-h-[90vh] object-contain rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
}
